// Copyright ©️ 2026 eVolve MEP, LLC

import { Avatar, Box, Divider, Group, Paper, Stack, Tabs, Title } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { isNil } from 'helpers/isNotNil';

import { useOptionalBendCreatorCartContext } from '../Context/useBendCreatorCartContext';
import { useBendCreatorFormContext } from '../Context/useBendCreatorFormContext';
import { BendDescription } from '../Description/BendDescription';
import { BendCreatorStepPanel, bendCreatorSteps, type BendCreatorStep } from './BendCreatorStepPanel';

type Props = {
  isEditing?: boolean;
  currentStep: BendCreatorStep | null;
  onChangeStep: (step: BendCreatorStep | null) => void;
};

const BendCreatorHeader = ({ isEditing }: Pick<Props, 'isEditing'>) => {
  const cartContext = useOptionalBendCreatorCartContext();
  const selectedItem = cartContext?.selectedItem;
  const isNew = isNil(selectedItem) && !isEditing;
  const { values } = useBendCreatorFormContext();

  return (
    <Group p="sm" wrap="nowrap">
      <EvolveIcon icon={isNew ? 'Add' : 'Edit'} color="primary" />
      <Stack gap={0}>
        <Title order={5}>{(isNew ? 'Create' : 'Edit') + ' Bend'}</Title>
        <BendDescription bend={values} />
      </Stack>
    </Group>
  );
};

export const CreatorSpecsNavigationPanel = ({ isEditing, currentStep, onChangeStep }: Props) => (
  <Paper withBorder shadow="sm" style={{ overflow: 'hidden', flexDirection: 'column' }} w="100%" mih={0} display="flex">
    <BendCreatorHeader isEditing={isEditing} />
    <Divider />
    <Box
      w="100%"
      maw="100%"
      flex="1 1 auto"
      mih={0}
      display="flex"
      style={{
        flexDirection: 'column',
        transform: `translate(${currentStep === null ? '0%' : '-100%'}, 0px)`,
        transition: 'transform 0.3s ease',
      }}
    >
      <Tabs
        flex="1 1 auto"
        mih={0}
        orientation="vertical"
        value={currentStep}
        onChange={(s) => onChangeStep(s as BendCreatorStep | null)}
        styles={{
          tab: { borderBottom: '1px solid var(--mantine-color-gray-1)' },
          tabLabel: { fontWeight: 500, fontSize: 'var(--mantine-font-size-md)', textAlign: 'left' },
        }}
      >
        <Tabs.List miw="100%">
          {bendCreatorSteps.map((step, i) => (
            <Tabs.Tab
              key={step}
              value={step}
              leftSection={
                <Avatar variant="light" styles={{ placeholder: { fontWeight: 'normal' } }}>
                  {i + 1}
                </Avatar>
              }
              rightSection={<EvolveIcon icon="ArrowRight" size="sm" color="gray" />}
            >
              {step}
            </Tabs.Tab>
          ))}
        </Tabs.List>
        {bendCreatorSteps.map((step, i) => (
          <Tabs.Panel key={step} value={step} miw="100%" style={{ overflowY: 'auto' }}>
            <BendCreatorStepPanel stepName={step} stepIndex={i} onChangeStep={onChangeStep} />
          </Tabs.Panel>
        ))}
      </Tabs>
    </Box>
  </Paper>
);
